import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { STAFF_LIST_TEST_IDS } from '../staffListTestIds';

const FILTERS = {
  orderNo: { paramKey: 'orderNo', inputType: 'text' },
  customer: { paramKey: 'name', inputType: 'text' },
  customerName: { paramKey: 'name', inputType: 'text' },
  orderDate: { paramKey: 'date', inputType: 'date' },
  ref: { paramKey: 'reference', inputType: 'text' },
  decedent: { paramKey: 'decedent', inputType: 'text' },
  // PGSS-5444: the Notes column has no filter, deliberately. It sent `customer_notes` to
  // /api/orders and the backend cannot search notes at all -- OrderController populates 31 fields
  // on the filter request and not one is note-related. The box took input, narrowed nothing, and
  // left staff concluding the records they wanted did not exist.
  //
  // Absent from FILTERS is how a column opts out: ColFilterCell does `if (!spec) return null`.
  // The column still shows its content and still sorts.
  //
  // 'customer_notes' stays in STAFF_LIST_COL_FILTER_PARAM_KEYS on purpose, so Reset still clears
  // it out of a bookmarked URL from before this change.
  //
  // To bring it back, build server-side filtering first: a field on OrderFilterRequest, a
  // predicate in OrderSpecification, and the parameter accepted by the controller.
  eta: { paramKey: 'deliveryETA', inputType: 'date' },
  weight: { paramKey: 'weight', inputType: 'number' },
  qty: { paramKey: 'quantity', inputType: 'number' },
  amount: { paramKey: 'amount', inputType: 'number' },
  paid: { paramKey: 'paid_amount', inputType: 'number' },
  due: { paramKey: 'due_amount', inputType: 'number' },
  dueDate: { paramKey: 'due_date', inputType: 'date' },
  shippedDate: { paramKey: 'shipped_date', inputType: 'date' },
  ctn: { paramKey: 'container_tracking_number', inputType: 'text' },
  // Container shipments list only (Order lists pick `container` from a list, see StaffListColFilter).
  container: { paramKey: 'container_number', inputType: 'text' },
  shipmentDate: { paramKey: 'shipment_date', inputType: 'date' },
  location: { paramKey: 'location', inputType: 'text' },
};

/**
 * Container shipments use their own API param names. No entry for capacity or noOfOrders:
 * neither is filterable (the legacy No of Orders paramKey was commented out).
 */
const CONTAINER_FILTER_PARAM_KEYS = {
  container: 'container_number',
  ctn: 'container_tracking_number',
  customer: 'customer_name',
  shipmentDate: 'shipment_date',
  eta: 'eta_date',
  weight: 'weight',
  location: 'location',
};

/** Sortable container columns. Not Order Type, No of Orders or Capacity — the API cannot sort them. */
export const STAFF_LIST_CONTAINER_SORT_KEYS = {
  container: 'container_number',
  ctn: 'container_tracking_number',
  customer: 'customer_name',
  shipmentDate: 'shipment_date',
  eta: 'eta_date',
  // PGSS-5655: no weight here. A container's weight is computed from its orders
  // (ShipmentService.getWeight), not a shipments column, so a weight sort returned 500.
  location: 'location',
  status: 'status',
};

/**
 * PGSS-5655: a container sort the API cannot run (e.g. `weight` from a bookmark made before Weight
 * stopped being sortable) is dropped from the URL instead of being sent, which returned 500.
 */
export function staffListContainerUnsupportedSort(search) {
  const sort = new URLSearchParams(search || '').get('sort');
  if (!sort) return false;
  const key = sort.split(',')[0];
  return !Object.values(STAFF_LIST_CONTAINER_SORT_KEYS).includes(key);
}

/** Current search without its sort. */
export function staffListSearchWithoutSort(search) {
  const sp = new URLSearchParams(search || '');
  sp.delete('sort');
  const next = sp.toString();
  return next ? `?${next}` : '';
}

export const STAFF_LIST_SORT_KEYS = {
  orderNo: 'orderNo',
  customer: 'name',
  customerName: 'name',
  orderDate: 'date',
  ref: 'reference',
  decedent: 'decedent',
  eta: 'deliveryETA',
  weight: 'weight',
  qty: 'quantity',
  amount: 'amount',
  paid: 'paid_amount',
  due: 'due_amount',
  status: 'status',
  // PGSS-5713: Days in status sorts by when the status last changed.
  statusDays: 'statusChangeTime',
};

/** Invoice list uses different API param names than the order-shaped list.
 *  paid/due fall through to FILTERS — same keys as FILTERS.paid / FILTERS.due. */
export function staffListFilterParamKey(filterKey, { listType, isSupplierType } = {}) {
  if (listType === 'container') {
    return CONTAINER_FILTER_PARAM_KEYS[filterKey] || null;
  }
  if (listType === 'invoice') {
    if (filterKey === 'orderNo') return 'invoice_no';
    if (filterKey === 'orderDate') return 'invoice_date';
    if (filterKey === 'amount') return 'invoice_amount';
    if (filterKey === 'dueDate') return 'due_date';
    if (filterKey === 'shippedDate') return 'shipped_date';
    if (filterKey === 'ctn') return 'container_tracking_number';
    if (filterKey === 'status') return 'invoice_status';
    if (filterKey === 'customer' && isSupplierType) return 'supplier_name';
    if (filterKey === 'customer') return 'name';
  }
  const spec = FILTERS[filterKey];
  if (!spec) return null;
  if (filterKey === 'customer' && isSupplierType) return 'supplier_name';
  return spec.paramKey;
}

export function staffListSortParamKey(colKey, { listType, isSupplierType } = {}) {
  if (listType === 'container') {
    return STAFF_LIST_CONTAINER_SORT_KEYS[colKey] || null;
  }
  if (listType === 'invoice') {
    if (colKey === 'orderNo') return 'invoice_no';
    if (colKey === 'orderDate') return 'invoice_date';
    if (colKey === 'amount') return 'invoice_amount';
    if (colKey === 'paid') return 'paid_amount';
    if (colKey === 'due') return 'due_amount';
    if (colKey === 'dueDate') return 'due_date';
    if (colKey === 'shippedDate') return 'shipped_date';
    // CTN is searchable but not sortable. Stage's sort rewrite has no mapping for
    // container_tracking_number, so a header click throws instead of sorting.
    if (colKey === 'status') return 'invoice_status';
    if (colKey === 'customer' && isSupplierType) return 'supplier_name';
    if (colKey === 'customer') return 'name';
  }
  if (colKey === 'customer' && isSupplierType) return 'supplier_name';
  return STAFF_LIST_SORT_KEYS[colKey] || null;
}

export const STAFF_LIST_COL_FILTER_PARAM_KEYS = [...new Set([
  ...Object.values(STAFF_LIST_SORT_KEYS),
  'order_type',
  'container_number',
  'no_container',
  'customer_notes',
  'supplier_name',
  'invoice_no',
  'invoice_date',
  'invoice_amount',
  'paid_amount',
  'due_amount',
  'due_date',
  'shipped_date',
  'container_tracking_number',
  'invoice_status',
  // Container shipments list.
  'customer_name',
  'shipment_date',
  'eta_date',
  'location',
  // PGSS-5634: customer invoices can be narrowed by the customer's payment type.
  // Reset must clear a bookmarked payment_type the same way it clears the other column searches.
  'payment_type',
])];

const inputStyle = {
  width: '100%',
  minWidth: 0,
  height: 28,
  padding: '0 8px',
  border: '1px solid #d5dbe4',
  borderRadius: 6,
  fontSize: 12,
  background: '#fbfcfd',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

/**
 * PGSS-5713: the order lists' container picker can also pick orders with no container yet (`no_container=true`),
 * e.g. orders sent to the supplier that were never put on a container. It replaces any container picked.
 */
export const NO_CONTAINER_OPTION = 'No container';

export function containerPickerValue(params) {
  return String(params?.no_container) === 'true' ? NO_CONTAINER_OPTION : params?.container_number;
}

export function containerPickerParams(next) {
  return next === NO_CONTAINER_OPTION
    ? { container_number: '', no_container: 'true' }
    : { container_number: next, no_container: '' };
}

export function optionLabel(item) {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  return String(item.name || item.container_number || item.value || '');
}

export function filterOptionNames(items, query) {
  const names = (items || []).map(optionLabel).filter(Boolean);
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return names;
  return names.filter((name) => name.toLowerCase().includes(needle));
}

export function selectedNames(value) {
  if (Array.isArray(value)) {
    return value.map(optionLabel).filter(Boolean);
  }
  return String(value || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
}

export function nextPickerValue(selected, name, multiple) {
  if (multiple) {
    return selected.includes(name)
      ? selected.filter((item) => item !== name)
      : [...selected, name];
  }
  return name === selected[0] ? '' : name;
}

export function emptyPickerValue(multiple) {
  return multiple ? [] : '';
}

export function pickerPanelPosition(rect, viewport, { minWidth = 220, maxHeight = 260, gap = 4, edge = 8 } = {}) {
  const viewW = viewport.width;
  const viewH = viewport.height;
  const width = Math.min(viewW - edge * 2, Math.max(minWidth, rect.width || 0));
  const left = Math.min(Math.max(edge, rect.left), Math.max(edge, viewW - width - edge));
  const spaceBelow = viewH - rect.bottom - gap;
  const spaceAbove = rect.top - gap;
  const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
  const available = openUp ? spaceAbove : spaceBelow;
  const height = Math.min(maxHeight, Math.max(80, available));
  const base = { position: 'fixed', left, width, zIndex: 1400 };
  if (openUp) {
    const bottom = Math.min(viewH - edge, Math.max(edge, viewH - rect.top + gap));
    const maxFit = Math.max(0, viewH - bottom - edge);
    return { ...base, bottom, maxHeight: Math.min(height, maxFit) };
  }
  const top = Math.min(viewH - edge, Math.max(edge, rect.bottom + gap));
  const maxFit = Math.max(0, viewH - top - edge);
  return { ...base, top, maxHeight: Math.min(height, maxFit) };
}

function NativeFilter({ paramKey, inputType, params, updateParams, testId }) {
  const value = params?.[paramKey] ?? '';
  return (
    <input
      type={inputType}
      value={value}
      placeholder="Search"
      data-testid={testId}
      style={inputStyle}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => updateParams(paramKey, event.target.value)}
    />
  );
}

/** Only one SearchablePicker panel open at a time (PGSS-5570). */
export const STAFF_LIST_PICKER_OPEN_EVENT = 'staff-list-picker-open';

/** Shared by Orders column filters and staff People tables (PGSS-5546 Products). */
export function SearchablePicker({
  options,
  value,
  multiple,
  onChange,
  testId,
  emptyLabel = 'All',
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const instanceId = `picker-${useId()}`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const selected = selectedNames(value);
  const visible = filterOptionNames(options, query);
  const summary = multiple
    ? (selected.length ? `${selected.length} selected` : emptyLabel)
    : (selected[0] || emptyLabel);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const place = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setPanelStyle(pickerPanelPosition(trigger.getBoundingClientRect(), {
      width: window.innerWidth,
      height: window.innerHeight,
    }));
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onMove = (event) => {
      if (event?.type === 'scroll' && panelRef.current?.contains(event.target)) return;
      place();
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // Capture phase: ColumnFilter stops mousedown bubbling so bubble listeners never see
    // clicks on a sibling picker trigger — which left several panels open (PGSS-5570).
    const onDoc = (event) => {
      const target = event.target;
      if (target?.closest?.('.MuiBackdrop-root')) return;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    const onOtherOpen = (event) => {
      if (event?.detail?.id !== instanceId) close();
    };
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener(STAFF_LIST_PICKER_OPEN_EVENT, onOtherOpen);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener(STAFF_LIST_PICKER_OPEN_EVENT, onOtherOpen);
    };
  }, [open, instanceId]);

  const pick = (event, name) => {
    event.stopPropagation();
    const next = nextPickerValue(selected, name, multiple);
    onChange(next);
    if (!multiple) close();
  };

  const clear = (event) => {
    event.stopPropagation();
    onChange(emptyPickerValue(multiple));
    close();
  };

  return (
    <div ref={rootRef} className="staff-list-picker" data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        className="staff-list-picker-trigger"
        aria-expanded={open}
        aria-haspopup="dialog"
        style={{
          ...inputStyle,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          color: selected.length ? '#111821' : '#8e8e8e',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        onClick={(event) => {
          event.stopPropagation();
          // Side effects stay outside the setState updater (React purity / StrictMode).
          if (open) {
            close();
            return;
          }
          window.dispatchEvent(new CustomEvent(STAFF_LIST_PICKER_OPEN_EVENT, {
            detail: { id: instanceId },
          }));
          setOpen(true);
        }}
      >
        {summary}
      </button>
      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          className="staff-list-picker-panel"
          data-testid={`${testId}-panel`}
          style={{
            ...panelStyle,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: '#fff',
            border: '1px solid #e3e8ef',
            borderRadius: 8,
            boxShadow: '0 12px 28px rgba(16,24,40,.14)',
          }}
        >
          <div className="staff-list-picker-toolbar">
            <input
              type="search"
              value={query}
              placeholder="Search"
              aria-label="Search options"
              style={inputStyle}
              onChange={(event) => setQuery(event.target.value)}
            />
            {selected.length > 0 && (
              <button
                type="button"
                data-testid={`${testId}-clear`}
                onClick={clear}
                style={{
                  marginTop: 6,
                  width: '100%',
                  height: 26,
                  border: '1px solid #d5dbe4',
                  borderRadius: 6,
                  background: '#fff',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  color: '#28323f',
                }}
              >
                Clear
              </button>
            )}
          </div>
          <div className="staff-list-picker-options">
            {visible.length === 0 && (
              <div style={{ padding: '6px 4px', fontSize: 12, color: '#8e8e8e' }}>No matches</div>
            )}
            {visible.map((name) => (
              multiple ? (
                <label
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 6px',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(name)}
                    onChange={(event) => pick(event, name)}
                  />
                  {name}
                </label>
              ) : (
                <button
                  key={name}
                  type="button"
                  onClick={(event) => pick(event, name)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '5px 6px',
                    border: 0,
                    background: selected.includes(name) ? '#eaf0ff' : 'transparent',
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: '#111821',
                  }}
                >
                  {name}
                </button>
              )
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function StaffListColFilter({
  filterKey,
  listType,
  params,
  updateParams,
  statusNames,
  containerNumbers,
  orderTypeNames,
  paymentTypeNames,
  isSupplierType,
}) {
  const testId = filterKey === 'type'
    ? STAFF_LIST_TEST_IDS.orderType
    : STAFF_LIST_TEST_IDS.colFilter(filterKey);
  if (filterKey === 'status') {
    const statusParam = listType === 'invoice' ? 'invoice_status' : 'status';
    return (
      <SearchablePicker
        options={statusNames}
        value={params?.[statusParam]}
        multiple
        onChange={(next) => updateParams(statusParam, next)}
        testId={testId}
      />
    );
  }
  /** PGSS-5409 (QA): the old list picks Order Type from the type list, not by typing it. */
  if (filterKey === 'type') {
    return (
      <SearchablePicker
        options={orderTypeNames}
        value={params?.order_type}
        onChange={(next) => updateParams('order_type', next)}
        testId={testId}
      />
    );
  }
  if (filterKey === 'container' && listType !== 'container') {
    return (
      <SearchablePicker
        options={[NO_CONTAINER_OPTION, ...(containerNumbers || [])]}
        value={containerPickerValue(params)}
        onChange={(next) => updateParams(containerPickerParams(next))}
        testId={testId}
      />
    );
  }
  // PGSS-5634: same multi-select the old invoice table used. The list already filters on payment_type.
  if (filterKey === 'paymentType') {
    return (
      <SearchablePicker
        options={paymentTypeNames}
        value={params?.payment_type}
        multiple
        onChange={(next) => updateParams('payment_type', next)}
        testId={testId}
      />
    );
  }
  const spec = FILTERS[filterKey];
  if (!spec) return null;
  if (listType === 'container' && !CONTAINER_FILTER_PARAM_KEYS[filterKey]) return null;
  const paramKey = staffListFilterParamKey(filterKey, { listType, isSupplierType }) || spec.paramKey;
  return (
    <NativeFilter
      paramKey={paramKey}
      inputType={spec.inputType}
      params={params}
      updateParams={updateParams}
      testId={testId}
    />
  );
}
